import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash, Play, Pause, DownloadSimple, CloudSlash } from "@phosphor-icons/react";
import { getOfflineTracks, removeCachedTrack } from "../lib/offline";
import { usePlayer } from "../context/PlayerContext";

export default function Downloads() {
  const [items, setItems] = useState([]);
  const { play, pause, isPlaying, track: current } = usePlayer();

  const reload = () => setItems(getOfflineTracks());

  useEffect(() => {
    reload();
    const h = () => reload();
    window.addEventListener("offline-registry-changed", h);
    return () => window.removeEventListener("offline-registry-changed", h);
  }, []);

  return (
    <div data-testid="downloads-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="overline text-[#B79C7B] mb-3 flex items-center gap-2">
        <DownloadSimple size={14} weight="duotone" /> Library
      </div>
      <h1 className="font-serif text-5xl text-ivory">Available Offline</h1>
      <p className="text-[#A1A1AA] mt-4 max-w-2xl">
        These tracks are saved on this device. You can listen to them even without an internet connection.
        Just open this page (or any track you've previously played) and tap play.
      </p>

      {items.length === 0 ? (
        <div className="mt-16 border border-dashed border-[#2A2A2D] p-16 text-center" data-testid="downloads-empty">
          <CloudSlash size={42} weight="duotone" className="mx-auto text-[#71717A]" />
          <h3 className="font-serif text-2xl text-ivory mt-4">Nothing saved yet</h3>
          <p className="text-sm text-[#A1A1AA] mt-2">
            Press play on any audio noha or majlis — it will be auto-saved here for offline listening.
          </p>
          <Link to="/category/nohay" className="inline-block mt-6 px-6 py-3 border border-[#B79C7B] text-[#B79C7B] hover:bg-[#B79C7B] hover:text-[#0A0A0B] transition-colors text-sm tracking-wider">
            Browse Nohay
          </Link>
        </div>
      ) : (
        <div className="mt-12 border border-[#2A2A2D]">
          <div className="grid grid-cols-[64px_1fr_140px_120px_60px] items-center px-5 py-3 bg-[#121214] border-b border-[#2A2A2D] overline text-[#71717A]">
            <div></div><div>Title</div><div>Type</div><div>Saved</div><div></div>
          </div>
          {items.map((t) => {
            const isCurrent = current?.id === t.id;
            const playingNow = isCurrent && isPlaying;
            const ready = !!t.cached_at;
            return (
              <div key={t.id} className="grid grid-cols-[64px_1fr_140px_120px_60px] items-center px-5 py-4 border-b border-[#2A2A2D]" data-testid={`download-row-${t.id}`}>
                <div className="w-12 h-12 bg-[#1A1A1D] border border-[#2A2A2D] overflow-hidden">
                  {t.thumbnail && <img src={t.thumbnail} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <Link to={`/track/${t.id}`} className="text-ivory hover:text-[#B79C7B] truncate block">{t.title}</Link>
                  <div className="text-xs text-[#71717A] truncate">{t.reciter_name}</div>
                </div>
                <div className="text-xs text-[#A1A1AA] uppercase">{t.type}</div>
                <div className="text-xs text-[#71717A]">
                  {ready ? new Date(t.cached_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "Saving…"}
                </div>
                <div className="flex justify-end gap-2">
                  {t.type === "audio" && ready && (
                    <button
                      onClick={() => (playingNow ? pause() : play(t))}
                      className="w-9 h-9 flex items-center justify-center border border-[#B79C7B] text-[#B79C7B] hover:bg-[#B79C7B] hover:text-[#0A0A0B]"
                      data-testid={`play-offline-${t.id}`}
                      aria-label="Play"
                    >
                      {playingNow ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                    </button>
                  )}
                  <button
                    onClick={() => removeCachedTrack(t)}
                    className="w-9 h-9 flex items-center justify-center border border-[#2A2A2D] text-[#71717A] hover:text-[#FF3B30] hover:border-[#FF3B30]"
                    data-testid={`remove-offline-${t.id}`}
                    aria-label="Remove"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
